import type { ReactNode } from "react";
import React, { createContext, useContext, useCallback } from "react";
import type { FeatureFlag } from "contexts/ConfigurationProvider/FeatureFlag";
import useAsyncRequest from "hooks/useAsyncRequest";
import { configurationApi } from "client/api-v2";

export interface ConfigurationState {
  isFeatureFlagEnabled(flag: FeatureFlag): boolean;
  loadedFeatureFlags: boolean;
  refresh: () => void;
}

interface ConfigurationProviderProps {
  children: ReactNode;
}

const ConfigurationProviderContext = createContext<ConfigurationState>({
  isFeatureFlagEnabled: (flag: FeatureFlag) => {
    return false;
  },
  loadedFeatureFlags: false,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  refresh: () => {},
});

export const useConfiguration = () => {
  return useContext(ConfigurationProviderContext);
};

export function ConfigurationProvider(props: ConfigurationProviderProps) {
  const { children } = props;

  const getFeatureFlags = useCallback(async () => {
    const { data } = await configurationApi.getV2ConfigurationFeatures();
    return data;
  }, []);

  const {
    data: featureFlags = {},
    refresh: refreshFeatureFlags,
    loading: loadingFeatureFlags,
  } = useAsyncRequest(getFeatureFlags);

  const isFeatureFlagEnabled = (flag: FeatureFlag) => {
    return featureFlags[flag] ?? false;
  };

  const refresh = () => {
    refreshFeatureFlags();
  };

  return (
    <ConfigurationProviderContext.Provider
      value={{ isFeatureFlagEnabled, loadedFeatureFlags: !loadingFeatureFlags, refresh }}
    >
      {children}
    </ConfigurationProviderContext.Provider>
  );
}

export const useFeatureFlag = (flag: FeatureFlag) => {
  const { isFeatureFlagEnabled } = useConfiguration();
  return isFeatureFlagEnabled(flag);
};
